import { ConfluenceDatabaseAri, ConfluenceWhiteboardAri } from '@atlassian/ari';
import { AnyAri } from '@atlassian/ari/any-ari';
import { ConfluenceCommentAri } from '@atlassian/ari/confluence/comment';

import {
  type ConfluenceService,
  getConfluenceService,
} from '@canvas/atlassian-service/confluence';

import {
  type ReactionsListRequestParams,
  type ReactionsClient,
  type ReactionSummary,
  type ReactionsRequestParams,
} from '../comment-reactions/reactions-client-interface';

export function getContainerIdFromAri(containerAri: string): string {
  if (ConfluenceDatabaseAri.check(containerAri)) {
    return ConfluenceDatabaseAri.parse(containerAri).databaseId;
  }
  return ConfluenceWhiteboardAri.parse(containerAri).whiteboardId;
}

/**
 * Client for interacting with reactions on comment threads.
 *
 * Uses {@link ConfluenceService} internally, translating between the data Confluence
 * expects and what the `@atlaskit/reactions` package expects.
 */
export class CommentThreadReactionsClient implements ReactionsClient {
  public constructor(private service: ConfluenceService) {}

  /**
   * Get reactions for a list of comments.
   *
   * @returns A map of comment ARIs to an array of reaction summaries for the comment.
   */
  public async getReactions({
    containerAri,
    commentAris,
  }: ReactionsListRequestParams): Promise<Record<string, ReactionSummary[]>> {
    const containerId = getContainerIdFromAri(containerAri);

    const reactions = await this.service.listCommentReactions(
      commentAris.map((commentAri) => {
        const commentId = ConfluenceCommentAri.parse(commentAri).commentId;
        return {
          containerId,
          commentId,
        };
      })
    );
    return reactions.reduce((acc, reaction) => {
      const commentReactions = acc[reaction.ari] ?? [];
      acc[reaction.ari] = commentReactions;

      for (let i = 0; i < reaction.reactionsSummaryForEmoji.length; i++) {
        commentReactions.push({
          ari: reaction.ari,
          // sometimes GraphQL returns returns a different container ARI than containerAri,
          // we want to use containerAri to ensure storage/lookup consistency in the reactions store
          containerAri,
          emojiId: reaction.reactionsSummaryForEmoji[i].emojiId,
          count: reaction.reactionsSummaryForEmoji[i].count,
          reacted: reaction.reactionsSummaryForEmoji[i].reacted,
        });
      }

      return acc;
    }, {} as Record<string, ReactionSummary[]>);
  }

  /**
   * Get detailed information about a reaction.
   *
   * @returns The reaction summary for the comment, including the list of users who have
   * reacted.
   */
  public async getDetailedReaction({
    commentAri,
    containerAri,
    emojiId,
  }: ReactionsRequestParams): Promise<ReactionSummary> {
    const containerId = getContainerIdFromAri(containerAri);
    const commentId = ConfluenceCommentAri.parse(commentAri).commentId;

    const reactionDetails = await this.service.getCommentReactionDetails({
      commentId,
      containerId,
      emojiId,
    });

    return {
      ari: reactionDetails.ari,
      // sometimes GraphQL returns returns a different container ARI than containerAri,
      // we want to use containerAri to ensure storage/lookup consistency in the reactions store
      containerAri,
      emojiId: reactionDetails.emojiId,
      count: reactionDetails.count,
      reacted: reactionDetails.reacted,
      users: reactionDetails.confluencePerson.map((user) => ({
        id: user.accountId,
        displayName: user.displayName,
      })),
    };
  }

  /**
   * Add a reaction to a comment from the current user.
   */
  public async confluence_addReaction({
    commentAri,
    containerAri,
    emojiId,
  }: ReactionsRequestParams): Promise<void> {
    const cloudId = AnyAri.parse(containerAri).cloudId;
    if (!cloudId) {
      throw new Error('cloudId is required for confluence_addReaction');
    }

    const containerId = getContainerIdFromAri(containerAri);
    const commentId = ConfluenceCommentAri.parse(commentAri).commentId;
    const containerType = AnyAri.parse(containerAri).resourceType.toUpperCase();

    await this.service.confluence_addCommentReaction({
      commentId,
      containerId,
      emojiId,
      containerType,
      cloudId,
    });
  }

  /**
   * Remove a reaction from a comment by the current user.
   */
  public async removeReaction({
    commentAri,
    containerAri,
    emojiId,
  }: ReactionsRequestParams): Promise<void> {
    const containerId = getContainerIdFromAri(containerAri);
    const commentId = ConfluenceCommentAri.parse(commentAri).commentId;
    const containerType = AnyAri.parse(containerAri).resourceType.toUpperCase();

    await this.service.deleteCommentReaction({
      commentId,
      containerId,
      emojiId,
      containerType,
    });
  }
}

let reactionsClient: ReactionsClient;

export const getReactionsClient = () => {
  if (!reactionsClient) {
    reactionsClient = new CommentThreadReactionsClient(getConfluenceService());
  }

  return reactionsClient;
};
